/**
 * Состояние авторизации.
 *
 * Access-токен не хранится в сторе и не попадает в localStorage — он лежит
 * в модуле api/client в памяти вкладки. Здесь только профиль пользователя
 * и состояние загрузки, на которое опирается интерфейс.
 */
import { currentLanguage, isLanguage, setLanguage } from "../i18n";
import { create } from "zustand";

import { refreshAccessToken, request, setAccessToken } from "../api/client";
import type { AuthResponse, RequestAccountDeletionResponse, User } from "../api/types";

type AuthStatus = "loading" | "authenticated" | "anonymous";

/** Данные формы регистрации. */
export interface RegisterInput {
  username: string;
  email: string;
  password: string;
  /** Согласие на обработку персональных данных — без него сервер откажет. */
  acceptPrivacyPolicy: boolean;
  /** Токен капчи. Пустой, если проверка на сервере выключена. */
  captchaToken?: string;
}

interface AuthState {
  user: User | null;
  status: AuthStatus;

  /** Попытка восстановить сессию по refresh-cookie при загрузке приложения. */
  restore: () => Promise<void>;
  login: (login: string, password: string, captchaToken?: string) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  logout: () => Promise<void>;
  /** Вызывается клиентом API, когда сессию восстановить не удалось. */
  clear: () => void;
  /**
   * Просьба удалить учётную запись. Возвращает, когда запись будет удалена.
   * Сервер уже закрыл все сессии, но локально выход — за вызывающим (clear):
   * человек сначала должен прочесть, до какого срока можно передумать.
   */
  requestDeletion: (password: string) => Promise<string | undefined>;
  /** Отмена удаления: сервер отдаёт новые токены уже без его признака. */
  cancelDeletion: () => Promise<void>;
}

/**
 * Язык из профиля важнее выбранного в этом браузере: человек мог сменить его
 * на другом устройстве, и письма уже приходят на нём.
 */
function applyUserLanguage(user?: User | null) {
  if (isLanguage(user?.language)) setLanguage(user.language);
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  status: "loading",

  restore: async () => {
    try {
      // Обновление идёт через общий для приложения вызов, а не собственным
      // fetch: refresh-токен одноразовый, и два параллельных обмена (например,
      // при повторном запуске эффекта) отозвали бы токен друг у друга.
      if (!(await refreshAccessToken())) {
        set({ user: null, status: "anonymous" });
        return;
      }

      const user = await request<User>("/api/v1/auth/me");
      applyUserLanguage(user);
      set({ user, status: "authenticated" });
    } catch {
      set({ user: null, status: "anonymous" });
    }
  },

  login: async (login, password, captchaToken) => {
    const data = await request<AuthResponse>("/api/v1/auth/login", {
      method: "POST",
      body: { login, password, captchaToken },
    });
    setAccessToken(data.accessToken ?? null);
    applyUserLanguage(data.user);
    set({ user: data.user ?? null, status: "authenticated" });
  },

  register: async (input) => {
    const data = await request<AuthResponse>("/api/v1/auth/register", {
      method: "POST",
      // Язык интерфейса на момент регистрации — на нём придёт письмо
      // с подтверждением.
      body: { ...input, language: currentLanguage() },
    });
    setAccessToken(data.accessToken ?? null);
    set({ user: data.user ?? null, status: "authenticated" });
  },

  logout: async () => {
    try {
      await request("/api/v1/auth/logout", { method: "POST", body: {} });
    } finally {
      // Даже если запрос не дошёл, локально пользователь должен выйти.
      setAccessToken(null);
      set({ user: null, status: "anonymous" });
    }
  },

  clear: () => {
    setAccessToken(null);
    set({ user: null, status: "anonymous" });
  },

  requestDeletion: async (password) => {
    const data = await request<RequestAccountDeletionResponse>("/api/v1/auth/me/deletion", {
      method: "POST",
      body: { password },
    });
    return data.deletionScheduledAt;
  },

  cancelDeletion: async () => {
    const data = await request<AuthResponse>("/api/v1/auth/me/deletion/cancel", {
      method: "POST",
      body: {},
    });
    setAccessToken(data.accessToken ?? null);
    set({ user: data.user ?? null, status: "authenticated" });
  },
}));

/** Есть ли у текущего пользователя доступ к админке. */
export function isStaff(user: User | null): boolean {
  return user?.role === "ROLE_MODERATOR" || user?.role === "ROLE_ADMIN";
}
